/**
 * HeroBackdrop — a near-imperceptible SVG motif used as a hero-only
 * background on each main page. Each variant gives a discreet visual
 * identity without competing with the editorial text.
 *
 * Constraints:
 *  - abstract only (no figurative imagery)
 *  - very low contrast (opacity 0.045 – 0.06 on the ink color)
 *  - lives on the right side of the hero so titles (left) stay crisp
 *  - hidden on small screens to keep mobile layout pristine
 *
 * Variants:
 *   "indexation"   — Home: bars fading from solid to ghost
 *   "circulation"  — Comprendre: thin curving flow lines
 *   "sequence"     — Méthode: four nodes connected on a discrete line
 *   "grid"         — Services: light organised block grid
 *   "documentary"  — Cadre juridique: stacked paper edge
 *   "modular"      — Structure: nested architectural rectangles
 *   "focal"        — Audit initial: corner brackets / framing reticle
 */

const INK = "#181A1D";

function Circulation() {
  // Motif cartographique discret : nœuds reliés par de fines lignes,
  // cohérent avec la cartographie du hero d'accueil (sans la reproduire).
  const nodes = [
    [180, 120], [360, 90], [540, 150], [700, 110],
    [260, 260], [460, 300], [640, 260],
    [200, 420], [420, 440], [620, 400], [720, 460],
  ];
  const edges = [
    [0, 1], [1, 2], [2, 3], [0, 4], [4, 5], [5, 2],
    [5, 6], [6, 3], [4, 7], [7, 8], [8, 5], [8, 9], [9, 6], [9, 10],
  ];
  const accent = new Set([2, 6, 9]);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[60%] pointer-events-none select-none"
      style={{ opacity: 0.16 }}
    >
      <g stroke={INK} strokeWidth="1" opacity="0.5">
        {edges.map(([a, b]) => (
          <line key={`${a}-${b}`} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} />
        ))}
      </g>
      {nodes.map(([x, y], i) => (
        <circle
          key={`${x}-${y}`}
          cx={x}
          cy={y}
          r={accent.has(i) ? 3 : 2.2}
          fill={accent.has(i) ? "#E94B3C" : INK}
          opacity={accent.has(i) ? 0.9 : 0.6}
        />
      ))}
    </svg>
  );
}

function Sequence() {
  // Four nodes joined by a horizontal rule, sober & methodical.
  const nodes = [120, 320, 520, 720];
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[60%] pointer-events-none select-none"
      style={{ opacity: 0.14 }}
    >
      <g stroke={INK} fill="none" strokeWidth="1">
        <line x1="60" y1="300" x2="760" y2="300" />
        {nodes.map((x, i) => (
          <g key={x}>
            <circle cx={x} cy="300" r="14" stroke={i === 3 ? "#E94B3C" : INK} />
            <circle cx={x} cy="300" r="3" fill={i === 3 ? "#E94B3C" : INK} stroke="none" />
          </g>
        ))}
      </g>
      <g
        fill={INK}
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontSize="10"
        letterSpacing="2"
        opacity="0.8"
      >
        {nodes.map((x, i) => (
          <text key={x} x={x - 8} y="340">
            0{i + 1}
          </text>
        ))}
      </g>
    </svg>
  );
}

function Grid() {
  // 4 × 4 small rounded rectangles, evoking organised blocks.
  const cols = 4;
  const rows = 4;
  const cell = 110;
  const gap = 22;
  const offsetX = 120;
  const offsetY = 60;
  const blocks = [];
  const accents = new Set(["1-2", "2-0", "3-3"]);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const isAccent = accents.has(`${r}-${c}`);
      blocks.push(
        <rect
          key={`${r}-${c}`}
          x={offsetX + c * (cell + gap)}
          y={offsetY + r * (cell + gap)}
          width={cell}
          height={cell}
          rx="6"
          fill={isAccent ? "#E94B3C" : "none"}
          fillOpacity={isAccent ? 0.18 : 0}
          stroke={isAccent ? "#E94B3C" : INK}
          strokeWidth="1"
        />
      );
    }
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 600"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[58%] pointer-events-none select-none"
      style={{ opacity: 0.13 }}
    >
      {blocks}
    </svg>
  );
}

function Documentary() {
  // Stacked thin horizontal lines, slight indents — a documentary edge.
  // Quelques repères rouges d'index sur la marge, langage cartographique.
  const lines = [];
  for (let i = 0; i < 22; i++) {
    const y = 70 + i * 20;
    const indent = i % 3 === 0 ? 0 : i % 3 === 1 ? 24 : 12;
    const accent = i === 4 || i === 12;
    lines.push(
      <line
        key={y}
        x1={120 + indent}
        y1={y}
        x2={720 - (i % 4) * 30}
        y2={y}
        stroke={accent ? "#E94B3C" : INK}
        strokeWidth={accent ? 2 : 1}
      />
    );
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[55%] pointer-events-none select-none"
      style={{ opacity: 0.14 }}
    >
      {/* faint left margin rule */}
      <line x1="100" y1="50" x2="100" y2="520" stroke={INK} strokeWidth="1" />
      {/* repères d'index rouges sur la marge */}
      <line x1="94" y1="150" x2="106" y2="150" stroke="#E94B3C" strokeWidth="2" />
      <line x1="94" y1="310" x2="106" y2="310" stroke="#E94B3C" strokeWidth="2" />
      {lines}
    </svg>
  );
}

function Modular() {
  // Nested rectangles — an architectural plan vibe.
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[58%] pointer-events-none select-none"
      style={{ opacity: 0.13 }}
    >
      <g fill="none" stroke={INK} strokeWidth="1">
        <rect x="80" y="60" width="640" height="440" />
        <rect x="80" y="60" width="380" height="260" />
        <rect x="80" y="60" width="220" height="150" />
        <rect x="460" y="60" width="260" height="440" />
        <rect x="80" y="320" width="380" height="180" />
        <line x1="300" y1="60" x2="300" y2="210" />
        <line x1="80" y1="210" x2="460" y2="210" />
        <line x1="460" y1="280" x2="720" y2="280" />
      </g>
    </svg>
  );
}

function Focal() {
  // Four corner brackets + crosshair — discreet framing reticle.
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[55%] pointer-events-none select-none"
      style={{ opacity: 0.15 }}
    >
      <g fill="none" stroke={INK} strokeWidth="1">
        {/* corner brackets */}
        <polyline points="140,90 100,90 100,130" />
        <polyline points="660,90 700,90 700,130" />
        <polyline points="140,470 100,470 100,430" />
        <polyline points="660,470 700,470 700,430" />
        {/* inner cross-hair */}
        <line x1="400" y1="220" x2="400" y2="340" />
        <line x1="340" y1="280" x2="460" y2="280" />
        <circle cx="400" cy="280" r="34" />
      </g>
      <circle cx="400" cy="280" r="5" fill="#E94B3C" />
    </svg>
  );
}

function Indexation() {
  // Vertical bars fading from solid to ghost — companion to home motif.
  const bars = [];
  const count = 22;
  for (let i = 0; i < count; i++) {
    const x = 140 + i * 24;
    const op = Math.max(0.05, 1 - i / (count - 1));
    bars.push(
      <line
        key={x}
        x1={x}
        y1={120}
        x2={x}
        y2={460}
        stroke={i === 0 ? "#E94B3C" : INK}
        strokeWidth={i === 0 ? 2 : 1}
        opacity={op}
      />
    );
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 560"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-y-0 right-0 h-full w-[55%] pointer-events-none select-none"
      style={{ opacity: 0.16 }}
    >
      {bars}
    </svg>
  );
}

const VARIANTS = {
  circulation: Circulation,
  sequence: Sequence,
  grid: Grid,
  documentary: Documentary,
  modular: Modular,
  focal: Focal,
  indexation: Indexation,
};

export default function HeroBackdrop({ variant }) {
  const Pattern = VARIANTS[variant];
  if (!Pattern) return null;
  return (
    <div
      data-testid={`hero-backdrop-${variant}`}
      aria-hidden="true"
      className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden"
    >
      <Pattern />
      {/* gentle fade so the pattern never reaches the page title on the left */}
      <div
        className="absolute inset-y-0 left-0 w-2/3 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(250,249,247,1) 0%, rgba(250,249,247,0.95) 30%, rgba(250,249,247,0.55) 65%, rgba(250,249,247,0) 100%)",
        }}
      />
    </div>
  );
}
